import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

// A cart line is one seller's offer. The same product can be in the cart from
// two shops, so lines are keyed by offer (falling back to product+shop for
// lines saved before offers existed) — never by product_id alone.
export const MAX_LINE_QTY = 100;

export const lineKey = (i) => i.offer_id || `${i.product_id}::${i.vendor_id}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pm_cart") || "[]"); } catch (e) { return []; }
  });
  const [preselectedAddressId, setPreselectedAddressId] = useState(() => localStorage.getItem("pm_cart_addr") || "");

  useEffect(() => {
    localStorage.setItem("pm_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (preselectedAddressId) localStorage.setItem("pm_cart_addr", preselectedAddressId);
    else localStorage.removeItem("pm_cart_addr");
  }, [preselectedAddressId]);

  const addItem = useCallback((product, qty = 1, addressId) => {
    if (addressId) setPreselectedAddressId(addressId);
    setItems((prev) => {
      const key = lineKey({ offer_id: product.offer_id, product_id: product.id, vendor_id: product.vendor_id });
      const existing = prev.find(i => lineKey(i) === key);
      if (existing) {
        return prev.map(i => i === existing ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.id,
        offer_id: product.offer_id,
        catalog_product_id: product.catalog_product_id,
        vendor_id: product.vendor_id,
        vendor_name: product.vendor_name,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        quantity: qty,
      }];
    });
  }, []);

  // Both take a line key (see lineKey), not a product id.
  const removeItem = useCallback((key) => setItems(prev => prev.filter(i => lineKey(i) !== key)), []);
  const updateQty = useCallback((key, quantity) => {
    if (quantity < 1) { setItems(prev => prev.filter(i => lineKey(i) !== key)); return; }
    setItems(prev => prev.map(i => lineKey(i) === key ? { ...i, quantity: Math.min(quantity, MAX_LINE_QTY) } : i));
  }, []);
  const clear = useCallback(() => { setItems([]); setPreselectedAddressId(""); }, []);

  // G6: apply current server prices/availability ({offer_id: {price, available}}).
  // Returns how many lines changed so the cart can tell the customer.
  const syncPrices = useCallback((byOffer) => {
    let changed = 0;
    setItems(prev => {
      const next = prev.map(i => {
        const cur = i.offer_id && byOffer[i.offer_id];
        if (!cur) return i;
        const unavailable = !cur.available;
        const price = typeof cur.price === "number" ? cur.price : i.price;
        if (price === i.price && unavailable === !!i.unavailable) return i;
        changed += 1;
        return { ...i, price, unavailable };
      });
      return changed ? next : prev;
    });
    return changed;
  }, []);

  // Move lines to another shop's offers (the "get it all from one shop"
  // suggestion): [{from_offer_id, offer_id, price}], plus the new shop.
  const moveToShop = useCallback((lines, vendor) => {
    const byFrom = Object.fromEntries(lines.map(l => [l.from_offer_id, l]));
    setItems(prev => {
      const merged = [];
      for (const i of prev) {
        const l = byFrom[i.offer_id];
        const next = l ? { ...i, offer_id: l.offer_id, price: l.price, vendor_id: vendor.vendor_id, vendor_name: vendor.vendor_name, unavailable: false } : i;
        const same = merged.find(m => lineKey(m) === lineKey(next));
        if (same) same.quantity += next.quantity; else merged.push({ ...next });
      }
      return merged;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("pm:logout", clear);
    return () => window.removeEventListener("pm:logout", clear);
  }, [clear]);

  const total = useMemo(() => items.reduce((s, i) => s + (i.unavailable ? 0 : i.price * i.quantity), 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQty, clear, syncPrices, moveToShop, total, count, preselectedAddressId, setPreselectedAddressId }),
    [items, addItem, removeItem, updateQty, clear, syncPrices, moveToShop, total, count, preselectedAddressId]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
